import {WriteButton} from '../../common/buttons/WriteActions';
import {useState} from 'react';
import {Box, Chip} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import {IRecipe} from '../../../models/Recipe/IRecipe';
import {ISubOrder} from '../../../models/Order/ISubOrder';
import EditSubOrder from '../editOrder/EditSubOrder';
import {IPackage} from "../../../models/Packages/IPackage";
import {useAppTranslation} from "@mybake/ui/hooks/useAppTranslation";
import {useCurrency} from '../../../hooks/useCurrency';
import {IDecoration} from '../../../models/Decorations/IDecoration';
import {subOrderEditorSx, subOrderSurfaceSx} from '../subOrderStyles';
import {DTO_STATE} from '@mybake/ui/common/dictionary';
import {IProductType} from '../../../models/ProductTypes/IProductType';

interface Props {
    existSubOrder: ISubOrder | undefined;
    recipes?: IRecipe[] | undefined;
    packages?: IPackage[] | undefined;
    decorations?: IDecoration[] | undefined;
    productTypes?: IProductType[];
    editedSubOrder: ISubOrder;
    handleOrderPrice?: () => void;
    deleteSubOrder: (subOrderId: number | string) => void;
    restoreSubOrder: (subOrderId: number | string) => void;
    saveSubOrderChanges: (subOrder: ISubOrder) => void;
    handleNewFiles: (files: Map<string, File>) => void;
    readOnly?: boolean;
}

const EditSubOrderHOC = ({
                             existSubOrder,
                             editedSubOrder,
                             recipes,
                             packages,
                             decorations,
                             productTypes,
                             deleteSubOrder,
                             restoreSubOrder,
                             saveSubOrderChanges,
                             handleNewFiles,
                             readOnly = false,
                         }: Props) => {
    const {t} = useAppTranslation();
    const {formatCurrency} = useCurrency();
    const buttonStyle = {lineHeight: '1.25rem' /* fontSize: '0.75rem' */};
    const [editMode, setEditMode] = useState(false);
    const isDeleted = editedSubOrder.state === DTO_STATE.DTO_DELETED;

    const handleDelete = () => {
        deleteSubOrder(editedSubOrder.id);
        setEditMode(false);
    };

    return (
        <Box sx={{...subOrderSurfaceSx, bgcolor: isDeleted ? 'error.light' : 'background.paper'}}>
            <Box>
                {t('recipes:recipe_title')}
                {existSubOrder?.recipeName}
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                {t('orders:decorations')}:
                <Box component="span" sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                    {!existSubOrder?.decorations?.length ? (
                        <Chip
                            label={t('orders:no_decoration_selected')}
                            size="small"
                            sx={{backgroundColor: 'warning.light', fontSize: '0.875rem', fontWeight: 500, borderRadius: '4px'}}
                        />
                    ) : existSubOrder.decorations.map((decoration) => (
                        <Chip
                            key={decoration.id}
                            label={`${decoration.name} ${decoration.quantity}`}
                            size="small"
                            sx={{backgroundColor: 'rgba(144, 238, 144, 0.7)', fontSize: '0.875rem', fontWeight: 500, borderRadius: '4px'}}
                        />
                    ))}
                </Box>
            </Box>
            {existSubOrder && existSubOrder?.fillingId !== 0 && (
                <Box>
                    {t('recipes:filling_title')}
                    {existSubOrder?.fillingName}
                </Box>
            )}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                {t('settings:packages')}:
                <Box component="span"
                     sx={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: 0.5, // небольшой отступ между текстом и чипами
                         color: existSubOrder?.packages?.length === 0 ? 'error.main' : 'inherit',
                         flexWrap: 'wrap'
                     }}>
                    {!existSubOrder?.packages?.length ? (<Chip
                        key="package_not_selected"
                        label={t('orders:no_package_selected')}
                        size="small"
                        sx={{
                            backgroundColor: 'warning.light',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            // Если нужно сохранить специфический радиус как в вашем Box (4px):
                            borderRadius: '4px',
                        }}
                    />) : existSubOrder?.packages?.map((el) => (
                        <Chip
                            key={el.packageId}
                            label={`${el.name} ${el.quantity} ${t('items:pcs')}`}
                            size="small"
                            sx={{
                                backgroundColor: 'rgba(144, 238, 144, 0.7)',
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                // Если нужно сохранить специфический радиус как в вашем Box (4px):
                                borderRadius: '4px',
                            }}
                        />
                    ))}
                </Box>
            </Box>
            <Box>
                {t('orders:order_description')}
                {existSubOrder?.description}
            </Box>
            <Box>
                {t('finance:order_price')}
                {formatCurrency(existSubOrder?.price ?? 0)}
            </Box>
            <Box>
                {t('finance:order_cost')}
                {formatCurrency(existSubOrder?.productCost ?? 0)}
            </Box>
            <Box>
                {t('items:count')}:&nbsp;
                {existSubOrder?.count}
            </Box>
            <Box>
                {t('orders:recipe_size_kg')}:&nbsp;{existSubOrder?.recipeCounter}
            </Box>

            {!readOnly && isDeleted && (
                <WriteButton
                    variant="outlined"
                    color="success"
                    startIcon={<RestoreIcon/>}
                    onClick={() => restoreSubOrder(editedSubOrder.id)}
                    sx={buttonStyle}
                >
                    {t('common:restore')}
                </WriteButton>
            )}

            {!readOnly && !isDeleted && (
                <WriteButton variant="outlined" onClick={() => setEditMode(!editMode)} sx={buttonStyle}>
                    {!editMode ? t('common:edit') : t('common:close')}
                </WriteButton>
            )}

            {!readOnly && !isDeleted && editMode && recipes && (
                <Box sx={subOrderEditorSx}>
                    <EditSubOrder
                        key={editedSubOrder.id}
                        subOrder={editedSubOrder}
                        recipes={recipes}
                        packages={packages}
                        decorations={decorations}
                        productTypes={productTypes}
                        deleteSubOrder={handleDelete}
                        saveSubOrderChanges={saveSubOrderChanges}
                        handleNewFiles={handleNewFiles}
                    />
                </Box>
            )}
        </Box>
    );
};

export default EditSubOrderHOC;
